import { del, keys } from 'idb-keyval';

const STORAGE_KEY = 'jurisapp:userId';
const LEGACY_PURGED_KEY = 'jurisapp:legacy-purged';

let currentUserId: string | null = null;

// --- Getters / Setters ---

/**
 * Define o userId atual e persiste em localStorage.
 * Passar null limpa o scope (logout).
 */
export function setCurrentUserId(id: string | null): void {
  currentUserId = id;
  if (id) {
    localStorage.setItem(STORAGE_KEY, id);
  }
}

/**
 * Retorna o userId atual. Lança erro se não definido.
 * Usar em operações de escrita que EXIGEM scope (addEntry, cacheDocument).
 */
export function getCurrentUserId(): string {
  if (!currentUserId) {
    throw new Error('userId não definido — operação offline requer usuário autenticado');
  }
  return currentUserId;
}

/**
 * Retorna o userId atual ou null se não definido.
 * Usar em operações de leitura que degradam silenciosamente.
 */
export function getUserIdOrNull(): string | null {
  return currentUserId;
}

/**
 * Lê userId do localStorage no bootstrap (antes do React).
 * Retorna o userId encontrado ou null.
 */
export function initUserScope(): string | null {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    currentUserId = stored;
  }
  return currentUserId;
}

/**
 * Remove userId do localStorage. Chamado no logout.
 */
export function clearUserIdFromStorage(): void {
  localStorage.removeItem(STORAGE_KEY);
  currentUserId = null;
}

// --- Helpers de nomes (storage per-user) ---

export function getOutboxDbName(): string {
  return `jurisapp-outbox-${getCurrentUserId()}`;
}

export function getQueryCacheKey(userId: string): string {
  return `jurisapp-query-cache-${userId}`;
}

export function getDocsCacheName(): string {
  return `jurisapp-docs-${getCurrentUserId()}`;
}

export function getDocMetaPrefix(): string {
  return `doc-meta:${getCurrentUserId()}:`;
}

export function getSyncLockName(): string {
  return `jurisapp-outbox-sync-${getCurrentUserId()}`;
}

// --- Migração de stores legados ---

/**
 * Limpa stores globais antigos (sem userId no nome).
 * Executada uma única vez — idempotente via flag em localStorage.
 */
export async function purgeLegacyStores(): Promise<void> {
  if (localStorage.getItem(LEGACY_PURGED_KEY)) return;

  await Promise.allSettled([
    // idb-keyval: query cache global
    del('jurisapp-query-cache'),

    // IndexedDB: outbox global
    deleteDatabase('jurisapp-outbox'),

    // Cache API: document cache global
    typeof caches !== 'undefined' ? caches.delete('jurisapp-docs') : Promise.resolve(),

    // idb-keyval: doc metas sem userId (prefixo antigo "doc-meta:" sem userId)
    clearLegacyDocMetas(),
  ]);

  localStorage.setItem(LEGACY_PURGED_KEY, '1');
}

/** Remove um banco IndexedDB pelo nome. */
export function deleteDatabase(name: string): Promise<void> {
  return new Promise((resolve) => {
    try {
      const request = indexedDB.deleteDatabase(name);
      request.onsuccess = () => resolve();
      request.onerror = () => resolve();
      request.onblocked = () => resolve();
    } catch {
      resolve();
    }
  });
}

/**
 * Remove chaves doc-meta:* que NÃO seguem o padrão per-user (doc-meta:{uuid}:{docId}).
 * Chaves legadas têm formato doc-meta:{docId} (sem UUID intermediário).
 */
async function clearLegacyDocMetas(): Promise<void> {
  try {
    const allKeys = await keys<string>();
    const uuidPattern = /^doc-meta:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}:/;
    const legacyKeys = allKeys.filter(
      (k) => typeof k === 'string' && k.startsWith('doc-meta:') && !uuidPattern.test(k),
    );
    await Promise.allSettled(legacyKeys.map((k) => del(k)));
  } catch {
    // Falha silenciosa
  }
}
